import React, { FC, useState, Suspense, useEffect, useDeferredValue, useRef } from 'react';
import { find, findIndex, flatMap, get, map, orderBy, toLower, uniq } from 'lodash';
import { Formik, FormikProps, FieldArray } from 'formik';
import { Plural, t, Trans } from '@lingui/macro';
import {
  CommodityComplexFilterInput,
  CommodityFilterByTerm,
  CrewMember,
  CrewMemberDiscipline,
  CrewMemberList,
  DeleteRodRequestInput,
  FilterOperator,
  Project,
  RodRequest,
  RodRequestFillerMaterial,
  RodRequestStatusCode,
  UpdateRodRequestInput,
  Weld,
  Scalars,
  CrewMemberSortByTerm,
} from '@bechtel/types';
import {
  SideSheet,
  SideSheetSize,
  SideSheetHeader,
  SideSheetBody,
  SideSheetFooter,
  SideSheetContentCol,
  Box,
  Text,
  AvatarSize,
  Icon,
  IconSize,
  useToastsDispatch,
  ToastType,
  ToastAction,
  Spinner,
  SpinnerSize,
  TextFieldAppearance,
  TextFieldSize,
  MultiSelectCheckbox,
} from '@bechtel/gds';
import { SectionHeader, useName, ErrorBoundary, SuspenseLoader, ErrorRetry, useAppUser } from '@bechtel/client-common';
import {
  FillerMaterialList,
  RodRequestFooterButtonStyled,
  PickupLocation,
  PickupTime,
  validationSchema,
  RodRequestFormikState,
  WeldProcedure,
  StencilSelector,
  useCrewMemberWithSupervisor,
  FillerMaterial,
  RodRequestComments,
  useWeldsByWeldProcedureQuery,
  WeldTagItem,
  useRodRoomPermissions,
} from '@bechtel/client-weld-common';
import { StencilRodRequests } from './stencil-rod-request';
import { EditWeldTab, EditWeldTabType, edltWeldTabList } from './types';
import {
  AvatarRegularStyled,
  SideSheetCloseStyled,
  DeleteButtonStyled,
  PickupTimeHeaderStyled,
  SideSheetContentColStyled,
  EditWeldTabStyled,
  TabButtonStyled,
  EditRodRequestDetailStyled,
  AssociatedWeldStyled,
  WeldSelectStyled,
} from './styled';
import { DELELTE_ROD_REQUEST, GET_PROJECT_COMMODITY_TYPES_BY_ID } from '@bechtel/client-gql';
import { useMutation, NetworkStatus, useLazyQuery, useApolloClient } from '@apollo/client';
import { DeleteRodRequestError, DeleteRodRequestErrorType } from './error';
import { UpdateRodRequestError, UpdateRodRequestErrorType } from '../error';
import { useUpdateRodRequest } from '~/hooks';
import { useLingui } from '@lingui/react';
//@ts-ignore needed for declaration generation
import { ReactComponent as RodRequestEmptyStateIcon } from '@bechtel/client-assets/dist/images/empty-weld-detail.svg';
import { DeleteRodRequestModal } from '~/components/atoms/confirm-delete';
import { useReactToPrint } from 'react-to-print';
import { PrintRodRequest } from './print-rod-request';
export interface EditRodRequestProps {
  crewMember: CrewMember;
  numRodRequests: Scalars['Int'];
  onClose: () => void;
}
const initialFormikValues: RodRequestFormikState = {
  welds: [],
  weldProcedure: '',
  stencils: [],
  fillerMaterials: [],
  pickupLocation: '',
  pickupTime: '',
  comments: '',
};

const UpdateRodRequestToast = (requestNumber: string) => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        {t`Rod Request with request number ${requestNumber} successfully updated.`}
      </Text>
    </Box>
  );
};

const DeleteRodRequestToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        {t`Rod request has been successfully deleted.`}
      </Text>
    </Box>
  );
};

const WeldTagSelectedItem = () => {
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }} px={{ sm: '200' }}>
      <Text family="graphik" size="400" color="grey.1000">
        +&nbsp;<Trans>Add Welds</Trans>
      </Text>
    </Box>
  );
};

export const EditMultipleRodRequest: FC<EditRodRequestProps> = ({ crewMember, numRodRequests, onClose }) => {
  const client = useApolloClient();
  const { getFullName } = useName();
  const { user } = useAppUser();
  const { i18n } = useLingui();
  const dispatchToast = useToastsDispatch();
  const [initialValues, setInitialValues] = useState<RodRequestFormikState>(initialFormikValues);
  const [weldSearchQuery, setWeldSearchQuery] = useState<string>('');
  const [deleteRodRequestError, setDeleteRodRequestError] = useState<DeleteRodRequestErrorType | null>(null);
  const [rodRequest, setRodRequest] = useState<RodRequest | undefined | null>(undefined);
  const { canDeleteRodRequest } = useRodRoomPermissions(
    rodRequest?.welds && rodRequest?.welds.length > 0 ? rodRequest.welds[0].commodityAssignees : []
  );
  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const [updateRodRequestError, setUpdateRodRequestError] = useState<UpdateRodRequestErrorType | null>(null);
  const [selectedTab, setSelectedTab] = useState<EditWeldTabType>(EditWeldTabType.WELD_DETAILS);
  const debouncedWeldSearchQuery = useDeferredValue(weldSearchQuery);
  const [deleteCompleted, setDeleteCompleted] = useState<boolean>(false);
  let form: FormikProps<RodRequestFormikState>;
  const weldsLimit = 20;
  const [loadWelds, { loading: weldsLoading, data: weldsData }] = useWeldsByWeldProcedureQuery(true);
  const weldList: Weld[] = map(get(weldsData, 'commodities.items', []), 'weld');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [
    getWelders,
    {
      data: crewMemberData,
      error: crewMemberError,
      loading: crewMemberLoading,
      refetch: crewMemberRefetch,
      networkStatus: crewMemberNetworkStatus,
      called: crewMemberCalled,
    },
  ] = useCrewMemberWithSupervisor();
  const componentRef = useRef();
  const welderList = get(crewMemberData, 'crewMembers.items', []) as CrewMemberList['items'];
  // TODO: remove below code to get the weldtypeid once BE starts providing welds by weld procedure
  const [getProject, { data: projectData, called: projectCalled }] = useLazyQuery<{
    projectsByIds: Project[];
  }>(GET_PROJECT_COMMODITY_TYPES_BY_ID, {
    notifyOnNetworkStatusChange: true,
  });
  const projectDetailArr: Project[] = get(projectData, 'projectsByIds', []);
  const projectDetail = projectDetailArr.length > 0 ? projectDetailArr[0] : null;
  const weldTypeId = find(projectDetail?.projectConfiguration.projectCommodityTypes, (item) => {
    return toLower(item.name) === 'weld';
  })?.id;
  const [
    updateRodRequest,
    { loading: updateRequestLoading, error: updateRequestError, data: updateRequestData },
  ] = useUpdateRodRequest(() => {
    setActionCompleted(true);
  });
  const [deleteRodRequest, { loading: mutationLoading }] = useMutation<{ input: DeleteRodRequestInput }>(
    DELELTE_ROD_REQUEST,
    {
      onCompleted: (data) => {
        setShowDeleteConfirm(false);
        setDeleteCompleted(true);
        const error = get(data, 'deleteRodRequest.error');
        if (error) {
          setDeleteRodRequestError(error);
        } else {
          setDeleteRodRequestError(null);
          onViewRodRequest();
          const toastAction: ToastAction = {
            type: 'ADD_TOAST',
            payload: {
              toastType: ToastType.INFO,
              content: () => DeleteRodRequestToast(),
              autoDismissTimeout: 5000,
            },
          };
          dispatchToast(toastAction);
          client.refetchQueries({
            include: ['crewProgressStats'],
          });
        }
      },
      onError: (error) => {
        setDeleteRodRequestError(error);
      },
    }
  );
  // TODO: remove below useEffect to call getProject once BE starts providing welds by weld procedure
  useEffect(() => {
    if (!user || !user.project || projectCalled) return;
    getProject({
      variables: {
        ids: [user?.project?.id],
      },
    });
  }, [getProject, projectCalled, user]);

  useEffect(() => {
    if (!rodRequest) return;
    const initialRodRequestFormikValues: RodRequestFormikState = {
      welds: rodRequest.welds,
      weldProcedureId: rodRequest.weldProcedure.id,
      fillerMaterials: formatFillerMaterials(rodRequest.fillerMaterials),
      stencils: rodRequest?.stencils ? map(rodRequest.stencils, (item) => item.stencil) : [],
      pickupLocation: rodRequest.rodRoom.id,
      //TODO pickup time can be past days so can't set the past values since TODAY/TOMORROW are the only options
      pickupTime: '',
      comments: rodRequest.comments.length > 0 ? orderBy(rodRequest.comments, 'createdAt', 'desc')[0].comment : '',
    };

    setInitialValues({ ...initialRodRequestFormikValues });
  }, [rodRequest]);

  useEffect(() => {
    if (!rodRequest || !rodRequest.weldProcedure || !weldTypeId || selectedTab === EditWeldTabType.WELD_DETAILS) return;

    const weldComplexFilter: CommodityComplexFilterInput = {
      and: [
        //TODO: uncomment below code once BE starts providing data based on weld procedure
        // {
        //   term: CommodityFilterByTerm.WELD_PROCEDURE_ID,
        //   operator: FilterOperator.EQUALS,
        //   value: rodRequest.weldProcedure.id,===
        // },
        // TODO: remove COMMODITY_TYPE_ID filter once BE starts providing welds by weld procedure
        {
          term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
          value: weldTypeId,
          operator: FilterOperator.EQUALS,
        },
      ],
    };
    if (debouncedWeldSearchQuery)
      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.TAG_NUMBER,
        value: debouncedWeldSearchQuery,
        operator: FilterOperator.CONTAINS,
      });
    loadWelds({
      variables: {
        input: {
          complexFilter: weldComplexFilter,
        },
        paging: {
          cursor: '1',
          limit: weldsLimit,
        },
      },
    });
  }, [debouncedWeldSearchQuery, loadWelds, rodRequest, weldTypeId, selectedTab]);

  useEffect(() => {
    if (!rodRequest || !rodRequest.stencils || rodRequest.stencils.length === 0 || crewMemberCalled) return;
    getWelders({
      variables: {
        input: {
          discipline: CrewMemberDiscipline.WELDING,
          sortBy: CrewMemberSortByTerm.WELDER_STENCIL,
          supervisorId: rodRequest.stencils[0]?.stencil?.supervisor?.id,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [crewMemberCalled, getWelders, rodRequest]);

  useEffect(() => {
    if (!actionCompleted) return;
    const error: UpdateRodRequestErrorType = get(updateRequestData, 'updateRodRequest.error', '') || updateRequestError;
    if (!error) {
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: () => UpdateRodRequestToast(get(updateRequestData, 'updateRodRequest.rodRequest.requestNumber', '')),
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
      onClose();
    } else {
      setUpdateRodRequestError(error);
    }
    setActionCompleted(false);
  }, [actionCompleted, dispatchToast, updateRequestData, updateRequestError, onClose]);

  const formatFillerMaterials = (fillerMaterials: RodRequestFillerMaterial[]) => {
    const result: FillerMaterial[] = [];
    fillerMaterials.forEach((fillerMaterialItem) => {
      if (fillerMaterialItem && fillerMaterialItem.diameters && fillerMaterialItem.diameters.length > 0)
        fillerMaterialItem.diameters.forEach((rodDiameterItem) => {
          result.push({
            fillerMaterial: {
              id: fillerMaterialItem.fillerMaterial.id,
              label: fillerMaterialItem.fillerMaterial.label,
            },
            diameter: {
              id: rodDiameterItem.id,
              label: rodDiameterItem.label,
            },
          });
        });
    });
    return result;
  };

  const onViewRodRequest = (rodRequest?: RodRequest) => {
    if (!rodRequest) {
      setRodRequest(null);
    } else {
      setRodRequest(rodRequest);
      form.setValues({
        welds: rodRequest.welds,
        weldProcedureId: rodRequest?.weldProcedure?.id,
        fillerMaterials: formatFillerMaterials(rodRequest.fillerMaterials),
        stencils: rodRequest?.stencils ? map(rodRequest.stencils, (item) => item.stencil) : [],
        pickupLocation: rodRequest.rodRoom.id,
        //TODO pickup time can be past days so can't set the past values since TODAY/TOMORROW are the only options
        pickupTime: '',
        comments: rodRequest.comments.length > 0 ? orderBy(rodRequest.comments, 'createdAt', 'desc')[0].comment : '',
      });
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef?.current,
  });

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values: RodRequestFormikState) => {
          const updateRodRequestInput: UpdateRodRequestInput = {
            fillerMaterialDiameterIds: map(flatMap(values.fillerMaterials, 'diameter'), 'id'),
            rodRequestId: rodRequest?.id,
            stencilIds: map(values.stencils, 'id'),
            weldIds: map(values.welds, 'id'),
            rodRoomId: values.pickupLocation,
            weldProcedureId: values?.weldProcedure?.id,
            pickUpTime: values.pickupTime,
            comments: values.comments,
          };
          updateRodRequest({
            variables: {
              input: updateRodRequestInput,
            },
          });
        }}
      >
        {(props: FormikProps<RodRequestFormikState>) => {
          const { handleSubmit, dirty, isValid, values, setFieldValue } = props;
          const { welds } = values;
          return (
            <SideSheet size={SideSheetSize.MEDIUM} onDismiss={onClose} visible data-testid="edit-rod-request">
              <SideSheetHeader title="" data-testid="side-sheet-header">
                <Box justifyContent={{ sm: 'space-between' }} mx={{ sm: '200' }} alignItems={{ sm: 'center' }}>
                  <Box data-testid="stencil-avatar">
                    <Box mr={{ sm: '400' }}>
                      <AvatarRegularStyled
                        size={AvatarSize.LARGE}
                        src={crewMember?.imageUrl ? crewMember?.imageUrl : undefined}
                      />
                    </Box>
                    <Box flexDirection={{ sm: 'column' }} data-testid="stencil-name">
                      <Text family="graphik" weight="600" size="600">
                        {getFullName(crewMember?.firstName, crewMember?.lastName)}
                      </Text>
                      <Box mt={{ sm: '300' }}>
                        <Text family="graphik" weight="600" size="400">
                          {crewMember?.badgeNumber}
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                  {numRodRequests > 0 && (
                    <Box alignItems={{ sm: 'center' }}>
                      <Box mr={{ sm: '700' }} px={{ sm: '600' }} data-testid="stencil-rod-request-count">
                        <Text family="graphik" weight="600" size="400">
                          {numRodRequests}
                        </Text>
                        &nbsp;
                        <Text family="graphik" size="400">
                          <Plural value={numRodRequests} one={`Rod Request`} other={`Rod Requests`} />
                        </Text>
                      </Box>
                    </Box>
                  )}
                </Box>
                <SideSheetCloseStyled data-testid="close-sidesheet" onClick={onClose}></SideSheetCloseStyled>
              </SideSheetHeader>
              <SideSheetBody data-testid="side-sheet-body">
                <SideSheetContentColStyled>
                  <StencilRodRequests
                    stencilId={crewMember?.id}
                    onViewRodRequest={onViewRodRequest}
                    selectedRodRequestId={rodRequest?.id}
                    actionCompleted={deleteCompleted}
                    setActionCompleted={setDeleteCompleted}
                    numRodRequests={numRodRequests}
                    onClose={onClose}
                  />
                </SideSheetContentColStyled>
                {rodRequest && (
                  <SideSheetContentCol
                    style={{ flexBasis: '56%', overflowY: 'hidden' }}
                    data-testid="SideSheetContentCol"
                  >
                    <EditWeldTabStyled>
                      {map(edltWeldTabList, (tabItem: EditWeldTab) => {
                        return (
                          <TabButtonStyled
                            selected={tabItem.value === selectedTab}
                            onClick={() => {
                              setSelectedTab(tabItem.value);
                            }}
                            key={tabItem.value}
                          >
                            {i18n._(tabItem.label)}
                            {`${tabItem.value === EditWeldTabType.ASSOCIATED_WELDS ? ` (${welds.length})` : ``}  `}
                          </TabButtonStyled>
                        );
                      })}
                    </EditWeldTabStyled>

                    {selectedTab == EditWeldTabType.VIEW && (
                      <ErrorBoundary>
                        <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
                          <Box
                            alignItems={{ sm: 'flex-start' }}
                            justifyContent={{ sm: 'center' }}
                            css={{ height: '550px', overflowY: 'auto' }}
                            style={{ height: '90%' }}
                          >
                            <PrintRodRequest rodRequest={rodRequest} ref={componentRef} />
                            <Box pt={{ sm: '450' }} css={{ cursor: 'pointer' }}>
                              <Icon icon="print" size={IconSize.MEDIUM} fillColor="blue.800" onClick={handlePrint} />
                            </Box>
                          </Box>
                        </Suspense>
                      </ErrorBoundary>
                    )}
                    {selectedTab == EditWeldTabType.ASSOCIATED_WELDS && (
                      <ErrorBoundary>
                        <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
                          <WeldSelectStyled>
                            <MultiSelectCheckbox
                              initialSelectedItems={welds}
                              selectedItems={welds}
                              showSearchBar={true}
                              items={weldList}
                              itemComponent={WeldTagItem}
                              selectedComponent={WeldTagSelectedItem}
                              size={TextFieldSize.LARGE}
                              showSelectAll={false}
                              confirmSelection={true}
                              displayKeyField="id"
                              appearance={TextFieldAppearance.NORMAL}
                              placeholder={t`Select Weld`}
                              loading={weldsLoading}
                              onSelectedItemsChange={(selectedWelds) => {
                                const updatedWelds = selectedWelds.filter(
                                  (selectedWeld) => !map(welds, 'id').includes(selectedWeld.id)
                                );
                                setFieldValue('welds', [...welds, ...updatedWelds]);
                              }}
                              onSearchInputChange={(searchQuery: string) => {
                                if (searchQuery.length > 0 && searchQuery.length < 3) return;
                                setWeldSearchQuery(searchQuery);
                              }}
                              term={weldSearchQuery}
                              setTerm={setWeldSearchQuery}
                            />
                          </WeldSelectStyled>
                          <AssociatedWeldStyled>
                            {map(welds, (weldItem: Weld) => {
                              return (
                                <Box
                                  key={weldItem.id}
                                  py={{ sm: '450' }}
                                  css={`
                                    border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
                                  `}
                                >
                                  <WeldTagItem
                                    item={weldItem}
                                    showDelete={true}
                                    onDelete={(weldId) => {
                                      const deleteWeldIndex = findIndex(welds, (item: Weld) => {
                                        return item.id === weldId;
                                      });
                                      if (deleteWeldIndex !== -1) {
                                        const updatedWelds = [...welds];
                                        updatedWelds.splice(deleteWeldIndex, 1);
                                        setFieldValue('welds', updatedWelds);
                                      }
                                    }}
                                  />
                                </Box>
                              );
                            })}
                          </AssociatedWeldStyled>
                        </Suspense>
                      </ErrorBoundary>
                    )}
                    {selectedTab == EditWeldTabType.WELD_DETAILS && (
                      <ErrorBoundary>
                        <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
                          <EditRodRequestDetailStyled>
                            <SectionHeader title={t`Weld Procedure(s)`} />
                            <WeldProcedure
                              data-testid="weld-procedure-list"
                              items={uniq(flatMap(values.welds, 'applicableWeldProcedures'))}
                              fieldName="weldProcedure"
                              onSelectedItemChange={(selectedWeldProcedureId: WeldProcedure['id']) => {
                                setFieldValue(
                                  'welds',
                                  selectedWeldProcedureId !== rodRequest.weldProcedure.id ? [] : [...rodRequest.welds]
                                );
                              }}
                            />
                            <SectionHeader title={t`Filler Material`} />
                            <FieldArray name="fillerMaterials">
                              {({ push, remove }) => {
                                return (
                                  <FillerMaterialList
                                    addFillerMaterial={push}
                                    removeFillerMaterial={remove}
                                    form={props}
                                  />
                                );
                              }}
                            </FieldArray>
                            <SectionHeader title={t`Stencil(s)`} />
                            {!crewMemberLoading && crewMemberError && (
                              <div data-testid="welder-error">
                                <ErrorRetry refetch={crewMemberRefetch}>
                                  <Trans>There was a problem loading Stencils</Trans>
                                </ErrorRetry>
                              </div>
                            )}
                            {!crewMemberError && (
                              <Box mx={{ sm: '450' }} my={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
                                <StencilSelector
                                  onSelectedItemsChange={(selectedItems) => {
                                    getWelders({
                                      variables: {
                                        input: {
                                          discipline: CrewMemberDiscipline.WELDING,
                                          sortBy: CrewMemberSortByTerm.WELDER_STENCIL,
                                          supervisorId:
                                            selectedItems && selectedItems.length > 0
                                              ? selectedItems[0]?.supervisor?.id
                                              : '',
                                        },
                                        paging: {
                                          cursor: '1',
                                          limit: 20,
                                        },
                                      },
                                    });
                                  }}
                                  fieldName="stencils"
                                  items={welderList}
                                  loading={crewMemberLoading || crewMemberNetworkStatus === NetworkStatus.refetch}
                                  hideDropDownIcon={true}
                                  data-testid="stencil-selector"
                                />
                              </Box>
                            )}
                            <SectionHeader title={t`Select Pickup Location`} />
                            <PickupLocation name="pickupLocation" />
                            <PickupTimeHeaderStyled
                              isRequestExpired={rodRequest?.currentStatusI18n === RodRequestStatusCode.EXPIRED}
                            >
                              <Text family="graphik" weight="600" size="400" color="grey.1000">
                                <Trans>Pick Up time</Trans>
                              </Text>
                              {rodRequest.currentStatusI18n === RodRequestStatusCode.EXPIRED && (
                                <Box ml={{ sm: '400' }}>
                                  <Icon icon="exclamation-circle" fillColor="bechtel.red" size={IconSize.MEDIUM} />
                                </Box>
                              )}
                            </PickupTimeHeaderStyled>
                            <PickupTime name="pickupTime" />
                            <SectionHeader title={t`Comments`} />
                            <RodRequestComments fieldName="comments" />
                            {canDeleteRodRequest && (
                              <Box my={{ sm: '500' }} mr={{ sm: '450' }} justifyContent={{ sm: 'flex-end' }}>
                                <DeleteButtonStyled
                                  onClick={() => {
                                    setShowDeleteConfirm(true);
                                  }}
                                >
                                  {mutationLoading ? (
                                    <Spinner size={SpinnerSize.SMALL} />
                                  ) : (
                                    <>
                                      <Box mr={{ sm: '400' }}>
                                        <Icon fillColor="red.500" icon="trash-alt" size={IconSize.SMALL} />
                                      </Box>
                                      <Trans>Delete Request</Trans>
                                    </>
                                  )}
                                </DeleteButtonStyled>
                              </Box>
                            )}
                          </EditRodRequestDetailStyled>
                        </Suspense>
                      </ErrorBoundary>
                    )}
                  </SideSheetContentCol>
                )}
                {!rodRequest && (
                  <SideSheetContentCol
                    style={{ flexBasis: '56%', overflowY: 'hidden' }}
                    data-testid="SideSheetContentCol"
                  >
                    <Box
                      flexDirection={{ sm: 'column' }}
                      alignItems={{ sm: 'center' }}
                      justifyContent={{ sm: 'center' }}
                      style={{ height: '100%' }}
                    >
                      <RodRequestEmptyStateIcon />
                      <Box mt={{ sm: '700' }} px={{ sm: '600' }} data-testid="no-rod-request">
                        <Text family="graphik" color="grey.600">
                          <Trans>Select a rod request</Trans>
                        </Text>
                      </Box>
                    </Box>
                  </SideSheetContentCol>
                )}
              </SideSheetBody>
              {rodRequest && (
                <SideSheetFooter>
                  <Box justifyContent={{ sm: 'flex-end' }}>
                    <Box mr={{ sm: '400' }}>
                      <RodRequestFooterButtonStyled
                        data-testid="request-cancel"
                        disabled={updateRequestLoading}
                        onClick={onClose}
                      >
                        <Trans>Cancel</Trans>
                      </RodRequestFooterButtonStyled>
                    </Box>
                    <RodRequestFooterButtonStyled
                      data-testid="request-submit"
                      disabled={updateRequestLoading || !dirty || !isValid}
                      onClick={() => handleSubmit()}
                    >
                      {updateRequestLoading ? <Spinner /> : <Trans>Save</Trans>}
                    </RodRequestFooterButtonStyled>
                  </Box>
                </SideSheetFooter>
              )}
            </SideSheet>
          );
        }}
      </Formik>
      {deleteRodRequestError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <DeleteRodRequestError error={deleteRodRequestError} close={() => setDeleteRodRequestError(null)} />
          </Suspense>
        </ErrorBoundary>
      )}
      {updateRodRequestError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <UpdateRodRequestError error={updateRodRequestError} close={() => setUpdateRodRequestError(null)} />
          </Suspense>
        </ErrorBoundary>
      )}
      {showDeleteConfirm && (
        <DeleteRodRequestModal
          onDelete={() =>
            deleteRodRequest({
              variables: {
                input: {
                  rodRequestId: rodRequest?.id,
                },
              },
            })
          }
          onCancel={() => setShowDeleteConfirm(false)}
          title={'Delete Rod Request'}
          textContent={`Are you sure you want to delete the rod request ${rodRequest?.requestNumber} ?`}
        ></DeleteRodRequestModal>
      )}
    </>
  );
};
